import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "@/components/waves/drops/identityDisplay.helpers";
import ParticipationIdentityProfileCard from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import type { ParticipationIdentityProfileCardVariant } from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { shortenAddress } from "@/helpers/address.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
type WaveLeaderboardIdentityVariant = "responsive" | "condensed";
interface WaveLeaderboardIdentityProps {
  readonly drop: ExtendedDrop;
  readonly variant: WaveLeaderboardIdentityVariant;
  readonly cardVariant?: ParticipationIdentityProfileCardVariant | undefined;
  readonly className?: string | undefined;
}
interface WaveLeaderboardIdentitySummaryProps {
  readonly profile: ApiProfileMin | null;
  readonly fallbackValue: string | null;
  readonly contextId?: string | number | undefined;
}
function WaveLeaderboardIdentitySummary(t0) {
  const $ = _c(36);
  const {
    profile,
    fallbackValue,
    contextId
  } = t0;
  const displayLabel = profile?.handle ?? profile?.primary_address ?? fallbackValue;
  if (!displayLabel) {
    return null;
  }
  let t1;
  if ($[0] !== displayLabel || $[1] !== profile) {
    t1 = profile ? `/${encodeURIComponent(displayLabel.toLowerCase())}` : null;
    $[0] = displayLabel;
    $[1] = profile;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rootHref = t1;
  const primaryAddress = profile?.primary_address;
  let t2;
  if ($[3] !== primaryAddress || $[4] !== profile) {
    t2 = !!profile?.handle && !!primaryAddress && primaryAddress.toLowerCase() !== profile.handle.toLowerCase();
    $[3] = primaryAddress;
    $[4] = profile;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const shouldShowAddress = t2;
  let t3;
  if ($[6] !== primaryAddress || $[7] !== shouldShowAddress) {
    t3 = shouldShowAddress ? shortenAddress(primaryAddress) : null;
    $[6] = primaryAddress;
    $[7] = shouldShowAddress;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const displayAddress = t3;
  let t4;
  if ($[9] !== displayLabel) {
    t4 = displayLabel.slice(0, 1);
    $[9] = displayLabel;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-iron-100">{t4}</span>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const avatarFallback = t5;
  let t6;
  if ($[13] !== avatarFallback || $[14] !== displayLabel || $[15] !== profile?.pfp || $[16] !== rootHref) {
    t6 = rootHref ? <Link href={rootHref} prefetch={false} onClick={_temp} className="tw-flex-shrink-0 tw-no-underline" aria-label={`View ${displayLabel}'s profile`}><ProfileAvatar pfpUrl={profile?.pfp ?? null} size={ProfileBadgeSize.SMALL} alt={`${displayLabel} avatar`} fallbackContent={avatarFallback} /></Link> : <ProfileAvatar pfpUrl={null} size={ProfileBadgeSize.SMALL} alt={`${displayLabel} avatar`} fallbackContent={avatarFallback} />;
    $[13] = avatarFallback;
    $[14] = displayLabel;
    $[15] = profile?.pfp;
    $[16] = rootHref;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== displayLabel || $[19] !== rootHref) {
    t7 = rootHref ? <Link href={rootHref} prefetch={false} onClick={_temp2} className="tw-max-w-full tw-text-sm tw-font-semibold tw-leading-none tw-text-iron-50 tw-no-underline desktop-hover:hover:tw-text-iron-300"><span className="tw-block tw-truncate">{displayLabel}</span></Link> : <span className="tw-break-all tw-text-sm tw-font-semibold tw-leading-none tw-text-iron-50">{displayLabel}</span>;
    $[18] = displayLabel;
    $[19] = rootHref;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== contextId || $[22] !== profile) {
    t8 = profile && <><UserCICAndLevel level={profile.level} size={UserCICAndLevelSize.SMALL} /><DropAuthorBadges profile={profile} tooltipIdPrefix={`leaderboard-identity-${contextId ?? profile.id}`} /></>;
    $[21] = contextId;
    $[22] = profile;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t7}{t8}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== displayAddress || $[28] !== primaryAddress) {
    t10 = displayAddress && <p title={primaryAddress} className="tw-mb-0 tw-mt-1 tw-font-mono tw-text-xs tw-tracking-[0.01em] tw-text-iron-400">{displayAddress}</p>;
    $[27] = displayAddress;
    $[28] = primaryAddress;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="tw-min-w-0 tw-flex-1">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t6) {
    t12 = <div data-testid="wave-leaderboard-identity-summary" className="tw-rounded-lg tw-border tw-border-iron-800/60 tw-bg-iron-900/40 tw-p-3"><div className="tw-flex tw-items-start tw-gap-3">{t6}{t11}</div></div>;
    $[33] = t11;
    $[34] = t6;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function _temp2(event_0) {
  return event_0.stopPropagation();
}
function _temp(event) {
  return event.stopPropagation();
}
export function WaveLeaderboardIdentity(t0) {
  const $ = _c(25);
  const {
    drop,
    variant,
    cardVariant,
    className
  } = t0;
  let t1;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t1 = getDropIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const identityProfile = t1;
  let t2;
  if ($[3] !== drop.metadata || $[4] !== drop.wave || $[5] !== identityProfile) {
    t2 = identityProfile ? null : getDropIdentityFallbackValue({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = identityProfile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const fallbackValue = t2;
  if (!identityProfile && !fallbackValue) {
    return null;
  }
  if (variant === "responsive" && identityProfile) {
    let t3;
    if ($[7] !== cardVariant || $[8] !== drop.id || $[9] !== identityProfile) {
      t3 = <div data-testid="wave-leaderboard-identity-full" className="tw-hidden lg:tw-block"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant={cardVariant} /></div>;
      $[7] = cardVariant;
      $[8] = drop.id;
      $[9] = identityProfile;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== drop.id || $[12] !== identityProfile) {
      t4 = <div className="lg:tw-hidden"><WaveLeaderboardIdentitySummary profile={identityProfile} fallbackValue={null} contextId={drop.id} /></div>;
      $[11] = drop.id;
      $[12] = identityProfile;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== className || $[15] !== t3 || $[16] !== t4) {
      t5 = <div className={className}>{t3}{t4}</div>;
      $[14] = className;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t3;
  if ($[18] !== drop.id || $[19] !== fallbackValue || $[20] !== identityProfile) {
    t3 = <WaveLeaderboardIdentitySummary profile={identityProfile} fallbackValue={fallbackValue} contextId={drop.id} />;
    $[18] = drop.id;
    $[19] = fallbackValue;
    $[20] = identityProfile;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== className || $[23] !== t3) {
    t4 = <div className={className}>{t3}</div>;
    $[22] = className;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}